import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../api/client';
import { Environment, EnvironmentConfig, ServerEnvironmentInfo, getCurrentEnvironment, setCurrentEnvironment, getEnvironmentConfig, getBackendEnvironmentName } from '../config/environments';
import { useAuth } from './AuthContext';

interface EnvironmentContextType {
  currentEnvironment: Environment;
  environmentConfig: EnvironmentConfig;
  serverEnvironmentInfo: ServerEnvironmentInfo | null;
  switchEnvironment: (env: Environment) => void;
  isEnvironmentReady: boolean;
  /** True while acquiring the OSDU token for a newly-selected environment. */
  isSwitching: boolean;
  refreshServerInfo: () => Promise<void>;
}

const EnvironmentContext = createContext<EnvironmentContextType | null>(null);

export const useEnvironment = () => {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error('useEnvironment must be used within EnvironmentProvider');
  }
  return context;
};

interface EnvironmentProviderProps {
  children: ReactNode;
}

export const EnvironmentProvider: React.FC<EnvironmentProviderProps> = ({ children }) => {
  const { refreshOsduToken, isAuthenticated } = useAuth();
  const [currentEnvironment, setCurrentEnvironmentState] = useState<Environment>(getCurrentEnvironment());
  const [environmentConfig, setEnvironmentConfig] = useState<EnvironmentConfig>(getEnvironmentConfig(currentEnvironment));
  const [serverEnvironmentInfo, setServerEnvironmentInfo] = useState<ServerEnvironmentInfo | null>(null);
  const [isEnvironmentReady, setIsEnvironmentReady] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const fetchServerEnvironmentInfo = async (env: Environment) => {
    try {
      const backendEnv = getBackendEnvironmentName(env);
      const response = await api.get<ServerEnvironmentInfo>('/environment/info', {
        headers: {
          'x-environment': backendEnv
        }
      });
      setServerEnvironmentInfo(response.data);
    } catch (error) {
      // Silently fail - environment info is for display only
      // The server will handle routing based on x-environment header
      setServerEnvironmentInfo(null);
    }
  };

  useEffect(() => {
    // Update environment config
    const config = getEnvironmentConfig(currentEnvironment);
    setEnvironmentConfig(config);

    // Store environment for server to read via x-environment header
    const backendEnv = getBackendEnvironmentName(currentEnvironment);
    sessionStorage.setItem('currentEnvironment', backendEnv);

    // Only fetch server environment info if user is authenticated
    if (isAuthenticated) {
      fetchServerEnvironmentInfo(currentEnvironment);
    }

    setIsEnvironmentReady(true);
  }, [currentEnvironment, isAuthenticated]);

  const switchEnvironment = async (env: Environment) => {
    if (env === currentEnvironment || isSwitching) return;

    setIsSwitching(true);

    // Persist the new environment first so the API client interceptor and the
    // token refresh below both pick up the new scope immediately.
    setCurrentEnvironment(env);
    const backendEnv = getBackendEnvironmentName(env);
    sessionStorage.setItem('currentEnvironment', backendEnv);

    // Acquire the OSDU token for the new environment before remounting the page.
    // This will trigger interactive login if the user hasn't consented to the new scope.
    try {
      console.log(`Switching to ${env} - acquiring OSDU token for new environment...`);
      await refreshOsduToken();
      console.log('OSDU token acquired successfully for new environment');
    } catch (error) {
      console.error('Failed to acquire OSDU token for new environment:', error);
      const newEnvConfig = getEnvironmentConfig(env);
      alert(`Failed to acquire token for ${newEnvConfig.displayName} environment. You may need to sign in again.`);
    }

    // Update React state last. Consumers keyed on `currentEnvironment` (e.g. the
    // routed page content) remount and re-fetch for the new environment while the
    // app shell (nav/header) stays put — no full page reload.
    setCurrentEnvironmentState(env);
    setIsSwitching(false);
  };

  const refreshServerInfo = async () => {
    await fetchServerEnvironmentInfo(currentEnvironment);
  };

  return (
    <EnvironmentContext.Provider
      value={{
        currentEnvironment,
        environmentConfig,
        serverEnvironmentInfo,
        switchEnvironment,
        isEnvironmentReady,
        isSwitching,
        refreshServerInfo,
      }}
    >
      {children}
    </EnvironmentContext.Provider>
  );
}; 